import { useEffect, useRef, useState } from "react";
import {
  getMonthStartEnd,
  removeDashFromString,
} from "../../../function/replaceString";
import styled, { css, useTheme } from "styled-components";
import * as Styled from "../../anonymouse/pages/LoginPageStyled";
import "../style/ProductionOverviewStyle.css";
import {} from "../../../component/calendar/function/CustomCalendarFunction";
import useFetch from "../../../api/useFetch";
import {
  deliveryAssignmentRequestApi,
  DeliveryAssignmentRequestParamType,
  deliveryManagerListGetApi,
  DeliveryManagerType,
  OrderListType,
  traderListGetApi,
  TraderType,
} from "../../../api/apiConfig";
import { useHeaderButtons } from "../../../component/header/HeaderButtonProvider";
import SquareButton from "../../../component/button/SquareButton";
import { BiWindowClose } from "react-icons/bi";
import { FiTrash2 } from "react-icons/fi";
import { DeliveryTable } from "../../../component/table/TableComponents/DeliveryTable";

//테이블에서 사용되는 데이터 타입 2종
export type DeliveryTableGroupType = {
  manager: DeliveryManagerType;
  deliveries: OrderListType[];
};

export const DeliveryListPage = () => {
  const theme = useTheme();

  const { setButtons } = useHeaderButtons();

  const [initialized, setInitialized] = useState<boolean>(false);

  const [periodStartDate, setPeriodStartDate] = useState<string>(
    getMonthStartEnd(new Date()).startDash
  );

  const [periodEndDate, setPeriodEndDate] = useState<string>(
    getMonthStartEnd(new Date()).endDash
  );

  const [traderSearchTerm, setTraderSearchTerm] = useState<string>("");

  //원본데이터
  const [data, setData] = useState<DeliveryTableGroupType[]>([]);

  //원본데이터 중 한 명의 담당자만 골라서 담아두는 변수
  const [selectedData, setSelectedData] =
    useState<DeliveryTableGroupType | null>(null);

  //거래처배열 담을 변수
  const [traderList, setTraderList] = useState<TraderType[]>([]);

  //선택된(포함된) 거래처 코드배열 담을 변수
  const [selectedTraders, setSelectedTraders] = useState<TraderType[]>([]);

  //거래처 드롭다운 관리
  const [traderDropdownOpen, setTraderDropdownOpen] = useState<boolean>(false);

  //배송담당자 목록 담을 변수
  const [deliveryManagers, setDeliveryManagers] = useState<
    DeliveryManagerType[]
  >([]);

  //선택된(포함된) 배송담당자 목록 담을 변수
  const [selectedManagers, setSelectedManagers] = useState<
    DeliveryManagerType[]
  >([]);

  //배송담당자 목록 조회
  const { fetch: fetchDeliveryManagerListGetApi } = useFetch({
    fetchFunction: deliveryManagerListGetApi,
    onSuccess: (data: DeliveryManagerType[]) => {
      setDeliveryManagers(data);
      setSelectedManagers(data);
    },
  });

  const { fetch: fetchDeliveryAssignmentRequestApi } = useFetch({
    fetchFunction: deliveryAssignmentRequestApi,
    onSuccess: (data: DeliveryTableGroupType[]) => {
      if (!initialized) setInitialized(true);
      setData(data);
      setSelectedData(data[0]);
    },
  });

  /** 거래처목록 조회 */
  const { fetch: fetchTraderListGetApi } = useFetch({
    fetchFunction: traderListGetApi,
    onSuccess: (data: TraderType[]) => setTraderList(data),
  });

  // 편의성 기능 추가 - 250214 치영
  const [highlightedTraderIndex, setHighlightedTraderIndex] =
    useState<number>(-1); // 거래처 인덱스 추적
  const traderRefs = useRef<(HTMLDivElement | null)[]>([]); // 거래처 항목의 DOM 요소 참조 배열

  // 필터링된 리스트 생성
  const filteredTraderList = traderList.filter((trader) =>
    traderSearchTerm.trim() ? trader.CUST_NAME.includes(traderSearchTerm) : true
  );

  useEffect(() => {
    if (!traderDropdownOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "ArrowDown":
          setHighlightedTraderIndex(
            (prev) => (prev + 1) % filteredTraderList.length
          );
          event.preventDefault();
          break;
        case "ArrowUp":
          setHighlightedTraderIndex((prev) =>
            prev > 0 ? prev - 1 : filteredTraderList.length - 1
          );
          event.preventDefault();
          break;
        case "Enter":
          if (
            highlightedTraderIndex >= 0 &&
            highlightedTraderIndex < filteredTraderList.length
          ) {
            selectTrader(filteredTraderList[highlightedTraderIndex]);
          }
          event.preventDefault();
          break;
        case "Escape":
          setTraderDropdownOpen(false);
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [filteredTraderList, highlightedTraderIndex, traderDropdownOpen]);

  // 거래처 선택 함수
  const selectTrader = (trader: TraderType) => {
    if (!selectedTraders.some((data) => data.CUST_CODE === trader.CUST_CODE)) {
      setSelectedTraders((prev) => [...prev, trader]);
    }
    // setTraderDropdownOpen(false); // 드롭다운 닫기
    setHighlightedTraderIndex(-1); // 하이라이트 초기화
  };

  // 하이라이트된 항목으로 자동 스크롤 이동
  useEffect(() => {
    if (
      highlightedTraderIndex >= 0 &&
      highlightedTraderIndex < traderRefs.current.length
    ) {
      traderRefs.current[highlightedTraderIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedTraderIndex]);

  useEffect(() => {
    fetchTraderListGetApi();
    fetchDeliveryManagerListGetApi();
  }, []);

  useEffect(() => {
    if (initialized) return;
    if (selectedManagers.length < 1) return;
    deliveryAssignmentRequest();
  }, [selectedManagers]);

  useEffect(() => {
    //헤더에 버튼 추가
    setButtons(
      <>
        <SquareButton
          text="조회하기"
          colorType="ACTIVE"
          onClick={() => deliveryAssignmentRequest()}
        />
      </>
    );
    // 페이지가 바뀌면 버튼 초기화
    return () => setButtons(null);
  }, [
    setButtons,
    periodStartDate,
    periodEndDate,
    selectedManagers,
    selectedTraders,
  ]);

  function deliveryAssignmentRequest() {
    const managerIds = selectedManagers.map((manager) => manager.id);
    const custCodes = selectedTraders.map((trader) => trader.CUST_CODE);

    const dto: DeliveryAssignmentRequestParamType = {
      startDt: removeDashFromString(periodStartDate),
      endDt: removeDashFromString(periodEndDate),
      manager: managerIds,
      custCode: custCodes,
    };
    fetchDeliveryAssignmentRequestApi(dto);
  }

  const TraderListArea = () => {
    return (
      <>
        {deliveryManagers.length > 0 ? (
          deliveryManagers.map((manager) => (
            <ManagerItem
              onClick={() => {
                setSelectedManagers((prev) =>
                  prev.includes(manager)
                    ? prev.filter((prevManager) => prevManager !== manager)
                    : [...prev, manager]
                );
              }}
              $included={selectedManagers.includes(manager)}
            >
              {manager.name}
            </ManagerItem>
          ))
        ) : (
          <div>배송담당자 정보가 없습니다.</div>
        )}
      </>
    );
  };

  return initialized ? (
    <>
      <PeriodContainer>
        {/* <Row>
          <RowLabel>담당자</RowLabel>
          <ManagerContainer>
            <TraderListArea />
          </ManagerContainer>
        </Row> */}
        <Row style={{ position: "relative" }}>
          <RowLabel>거래처 지정</RowLabel>
          <RowContent>
            <div
              style={{
                cursor: "pointer",
                width: "fit-content",
                display: "flex",
                flexDirection: "row",
                alignContent: "center",
              }}
            >
              <>
                <div
                  onClick={() => setTraderDropdownOpen(!traderDropdownOpen)}
                  style={{
                    cursor: "pointer",
                    minWidth: "80px",
                    textAlign: "center",
                  }}
                >
                  추가하기
                </div>
                <SelectedProdItemContainer>
                  {selectedTraders.length > 0 &&
                    selectedTraders.map((trader) => (
                      <SelectedProdItem>
                        <FiTrash2
                          size={15}
                          color={theme.colors.red}
                          style={{
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setSelectedTraders((prev) =>
                              prev.filter((prevItem) => prevItem !== trader)
                            )
                          }
                        />
                        <SelectedProdItem>{trader.CUST_NAME}</SelectedProdItem>
                      </SelectedProdItem>
                    ))}
                </SelectedProdItemContainer>
              </>

              <DropdownBase show={traderDropdownOpen}>
                <ProductDropdownHeader>
                  <div
                    style={{
                      height: "26px",
                      backgroundColor: "#1f48d4",
                      marginBottom: "3px",
                      textAlign: "end",
                      alignContent: "center",
                      paddingRight: "5px",
                    }}
                  >
                    <BiWindowClose
                      size={20}
                      color="white"
                      style={{ cursor: "pointer" }}
                      onClick={() => setTraderDropdownOpen(false)}
                    />
                  </div>
                  <div style={{ padding: "3px", display: "flex" }}>
                    <Styled.Input
                      placeholder="제품명 검색"
                      value={traderSearchTerm}
                      style={{ marginBottom: "5px", maxHeight: "18px" }}
                      onChange={(e) => setTraderSearchTerm(e.target.value)}
                    />
                  </div>
                </ProductDropdownHeader>
                <ProductDropdownBody>
                  {traderList.length > 0 ? (
                    traderList
                      .filter((trader) =>
                        traderSearchTerm?.trim()
                          ? trader.CUST_NAME.includes(traderSearchTerm)
                          : true
                      )
                      .map((trader, index) => (
                        <DropdownItem
                          ref={(el) => (traderRefs.current[index] = el)} // 각 항목에 ref 할당
                          key={trader.CUST_CODE} // 혹은 적절한 유일 식별자 사용
                          onClick={() => {
                            !selectedTraders.some(
                              (data) => data.CUST_CODE === trader.CUST_CODE
                            ) &&
                              setSelectedTraders((prev) => [...prev, trader]);
                          }}
                          included={selectedTraders.some(
                            (data) => data.CUST_CODE === trader.CUST_CODE
                          )}
                          highlighted={index === highlightedTraderIndex} // 하이라이트 적용
                        >
                          {trader.CUST_NAME}
                        </DropdownItem>
                      ))
                  ) : (
                    <div>거래처 정보가 없습니다</div>
                  )}
                </ProductDropdownBody>
              </DropdownBase>
            </div>
          </RowContent>
        </Row>
        <Row>
          <RowLabel>조회시작일</RowLabel>
          <RowContent>
            <DateInput
              type="date"
              value={periodStartDate}
              onChange={(e) => {
                setPeriodStartDate(e.target.value);
              }}
            />
          </RowContent>
          <RowLabel>조회종료일</RowLabel>
          <RowContent>
            <DateInput
              type="date"
              value={periodEndDate}
              onChange={(e) => {
                setPeriodEndDate(e.target.value);
              }}
            />
          </RowContent>
        </Row>
      </PeriodContainer>
      <div style={{ height: "30px" }} />
      <NameSection>
        {data.length > 0 &&
          data.map((group, index) => (
            <NameSelect
              selected={group.manager === selectedData?.manager}
              isFirstItem={index === 0}
              isLastItem={index + 1 === data.length}
              isFixed={group.manager === selectedData?.manager}
              onClick={() => setSelectedData(group)}
            >
              {group.manager.name}
            </NameSelect>
          ))}
      </NameSection>
      {/* <div
        style={{ width: '550px', height: '300px', backgroundColor: 'white' }}
      >
        <DeliveryChart />
      </div> */}
      <DeliveryTable data={selectedData?.deliveries || []} />
      {/* <ProductionTable
        periodStartDate={periodStartDate}
        periodEndDate={periodEndDate}
        itemGruopOption={itemGroupOption}
        selectedItemList={selectedProductItemList}
        selectedWorkbay={selectedWorkbay}
      /> */}
    </>
  ) : (
    <h4>조회중입니다...</h4>
  );
};

const NameSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const NameSelect = styled.div<{
  selected: boolean;
  isFirstItem: boolean;
  isLastItem: boolean;
  isFixed: boolean;
}>`
  cursor: pointer;

  padding: 8px 12px;
  font-weight: 600;
  border-bottom: ${({ isFixed, theme }) =>
    isFixed ? "1px solid transparent" : `1px solid ${theme.colors.red}`};
  align-content: center;
  text-align: center;
  background-color: ${({ selected, theme }) =>
    selected ? theme.colors.white : theme.colors.white_odd};

  border-top-left-radius: ${({ isFirstItem }) => (isFirstItem ? "15px" : 0)};
  border-top-right-radius: ${({ isLastItem }) => (isLastItem ? "15px" : 0)};

  &:hover {
    background-color: rgb(255 255 255 / 4%);
  }
`;

const CalendarWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  /* justify-content: center; */
  /* align-items: center; */
  gap: 20px;
`;

const CursorClickArea = styled.div`
  width: 60px;
  height: 60px;
  align-content: center;
  text-align: center;
  cursor: pointer;
`;

const DropdownBase = styled.div<{ show: boolean }>`
  font-weight: 500;
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  overflow: hidden;
  position: absolute;
  background-color: white;
  left: 100%;
  margin-left: 15px;
  top: 0px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(71, 71, 71, 0.678);
  transform: ${({ show }) => (show ? "scale(1)" : "scale(0)")};
  transform-origin: top left;
  transition: transform 0.3s ease, width 0.3s ease, height 0.3s ease;
`;

const ManagerContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  flex-grow: 1;
  padding: 4px 10px;
  background-color: white;
`;

const ManagerItem = styled.div<{ $included: boolean }>`
  padding: 3px 7px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.charcoal};
  border: 1px solid black;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontsize.header_semi};
  cursor: pointer;
  background-color: ${({ $included }) =>
    $included ? "#d9f0ff" : "transparent"};
`;

const DropdownItem = styled.div<{ included: boolean; highlighted?: boolean }>`
  width: 100%;
  padding: 4px 1px;
  ${({ included }) =>
    included
      ? css`
          background-color: ${({ theme }) => theme.colors.lime};
        `
      : css`
          cursor: pointer;
          &:hover {
            background-color: ${({ theme }) => theme.colors.lightGray};
          }
        `}

  ${({ highlighted }) =>
    highlighted &&
    css`
      background-color: ${({ theme }) => theme.colors.blue};
      color: white;
    `}
`;

const PeriodContainer = styled.div`
  max-width: 800px;
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-items: stretch;

  &:not(:last-child) {
    border-bottom: 1px solid #b8b8b8;
  }
`;

const RowLabel = styled.div`
  flex: 0 0 120px;
  background-color: #d9d9d9;
  font-size: 15px;
  align-content: center;
  text-align: center;
  padding: 4px 0px;
  font-weight: 700;
`;

const RowContent = styled.div`
  flex-grow: 1;
  padding: 4px 10px;
  background-color: white;
`;

const ProductDropdownHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ProductDropdownBody = styled.div`
  position: relative;
  gap: 6px;
  margin: 7px;
  display: flex;
  flex-direction: column;
  width: 220px;
  overflow-x: hidden;
  overflow-y: scroll;
  min-height: 350px;
  max-height: 500px;
  background-color: ${({ theme }) => theme.colors.whiteGray};
`;

const SelectedProdItemContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const SelectedProdItem = styled.div`
  display: flex;
  padding: 0px 4px;
  flex-direction: row;
  gap: 3px;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
`;

const DateInput = styled.input`
  width: 100%;
  color: #595b60;
  font-weight: 700;
  font-size: 14px;
  text-align: center;
  box-sizing: border-box;
  border: none;
  cursor: pointer;

  /* Webkit 브라우저에서 달력 아이콘 숨기기 */
  /* &::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }

  /* Firefox 브라우저에서 달력 아이콘 숨기기 */
  /*-moz-appearance: textfield; */
`;
